import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Zap, BellRing, CreditCard, ShieldCheck, Power, PauseCircle, RotateCcw, Mail,
} from "lucide-react";

/**
 * /dashboard/diploks/auto-topup
 * ----------------------------------------------------------------------
 * Per-user Auto Top-Up settings.  Two modes:
 *  - smart_reminder: when balance < threshold, send an in-app + email
 *    reminder with a one-tap top-up CTA.  NO silent charges.
 *  - true_auto: off-session charges via Stripe (foreign) or scheduled
 *    via Razorpay subscription (INR).  Requires saved payment method.
 */
const TIERS = [
  { key: "starter_99", label: "Starter (15 Diploks)" },
  { key: "basic_299", label: "Basic (50 Diploks)" },
  { key: "growth_999", label: "Growth (200 Diploks)" },
  { key: "power_3999", label: "Power (1,000 Diploks)" },
];

const CURRENCIES = ["INR", "USD", "AED", "EUR", "GBP", "HKD", "SGD", "MYR", "IDR"];

export default function AutoTopUpSettingsPage() {
  const [config, setConfig] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reminders, setReminders] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/diploks/auto-topup");
      setConfig(r.data?.config);
      setBalance(r.data?.current_balance);
      const r2 = await api.get("/diploks/auto-topup/reminders");
      setReminders(r2.data?.reminders || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (partial) => {
    setSaving(true);
    try {
      const r = await api.patch("/diploks/auto-topup", partial);
      setConfig(r.data?.config);
      toast.success("Auto top-up settings saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setupStripeMandate = async () => {
    if (!config) return;
    try {
      const r = await api.post("/diploks/auto-topup/setup-intent",
        { currency: config.currency || "USD" });
      const { mode, client_secret } = r.data;
      if (mode === "mock") {
        // Synthetic confirm path for preview environments
        await api.post("/diploks/auto-topup/setup-intent/confirm", {
          setup_intent_id: r.data.setup_intent_id,
          payment_method_id: "pm_mock_card_visa",
        });
        toast.success("Mock payment method saved. Auto top-up is now ACTIVE.");
        await load();
        return;
      }
      // Live path needs Stripe Elements — we surface the client secret
      // here for the user to complete via a deeper flow we ship later.
      toast.message("Open the Stripe-secured form to save your card.", {
        description: "client_secret " + client_secret.substring(0, 18) + "…",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startRazorpaySubscription = async (planId) => {
    if (!planId) {
      toast.error("Razorpay plan ID is required to start a mandate.");
      return;
    }
    try {
      const r = await api.post("/diploks/auto-topup/razorpay-subscription", {
        plan_id: planId,
        tier_key: config?.tier_key || "basic_299",
        threshold_diploks: config?.threshold_diploks || 50,
      });
      if (r.data?.mode === "mock") {
        toast.success("Mock Razorpay subscription registered.");
        await load();
        return;
      }
      if (r.data?.short_url) {
        window.open(r.data.short_url, "_blank");
        toast.message("Complete the mandate in the new tab.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const pauseCfg = async () => {
    try {
      await api.post(`/diploks/auto-topup/${config.id}/pause`);
      toast.success("Auto top-up paused.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resumeCfg = async () => {
    try {
      await api.post(`/diploks/auto-topup/${config.id}/resume`);
      toast.success("Auto top-up resumed.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelCfg = async () => {
    if (!window.confirm("Cancel auto top-up? Your saved payment method will be revoked.")) return;
    try {
      await api.post(`/diploks/auto-topup/${config.id}/cancel`);
      toast.success("Auto top-up cancelled.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <p className="text-sm text-slate-500" data-testid="autotopup-loading">
          Loading auto top-up settings…
        </p>
      </DashboardLayout>
    );
  }
  if (!config) return null;

  const hasMandate = !!(config.razorpay_subscription_id
                      || config.stripe_payment_method_id);
  const isFallback = config.status === "fallback_required";

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="autotopup-settings-page">
        <Link to="/dashboard/diploks/personal/wallet"
              className="text-xs text-slate-500 hover:text-[#0F4C81] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to My Diploks
        </Link>

        <div className="mt-3 mb-6">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Auto Top-Up
          </p>
          <h1 className="mt-1 text-3xl font-bold text-[#0A192F] tracking-tight">
            Never run out of Diploks
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Choose how Diplomexa replenishes your wallet when it gets low.
          </p>
        </div>

        {/* Status banner */}
        <div className={`rounded-2xl px-5 py-4 mb-6 ${
          config.status === "active" ? "bg-emerald-50 border border-emerald-200"
            : isFallback ? "bg-rose-50 border border-rose-200"
            : "bg-slate-50 border border-slate-200"
        }`} data-testid="autotopup-status-banner">
          <p className="text-xs uppercase tracking-[0.22em] font-bold">
            Current status
          </p>
          <p className="mt-1 text-sm font-semibold text-[#0A192F]">
            {config.status === "active" && "Active"}
            {config.status === "off" && "Off"}
            {config.status === "paused" && "Paused"}
            {config.status === "setup_started" && "Setup started — finish your mandate"}
            {isFallback && (
              <>
                Mandate disabled after {config.consecutive_failures} failed
                attempt{config.consecutive_failures > 1 ? "s" : ""}. Reminders only.
              </>
            )}
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Current balance: <span className="font-bold">{balance}</span> Diploks ·
            Threshold: {config.threshold_diploks} Diploks · Mode: {config.mode}
          </p>
        </div>

        {/* Mode chooser */}
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <button
            type="button"
            onClick={() => patch({ mode: "smart_reminder" })}
            data-testid="autotopup-mode-reminder"
            className={`text-left rounded-2xl border p-5 transition-all ${
              config.mode === "smart_reminder"
                ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/30"
                : "border-slate-200 hover:border-slate-300"
            } bg-white`}
          >
            <BellRing className="h-5 w-5 text-[#0F4C81] mb-2" />
            <p className="text-sm font-bold text-[#0A192F]">Smart reminder</p>
            <p className="mt-1 text-xs text-slate-600 leading-relaxed">
              Email + in-app reminder when you fall below the threshold. No
              silent charges. One-tap top-up CTA.
            </p>
          </button>

          <button
            type="button"
            onClick={() => patch({ mode: "true_auto" })}
            data-testid="autotopup-mode-true-auto"
            className={`text-left rounded-2xl border p-5 transition-all ${
              config.mode === "true_auto"
                ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/30"
                : "border-slate-200 hover:border-slate-300"
            } bg-white`}
          >
            <Zap className="h-5 w-5 text-[#D4AF37] mb-2" />
            <p className="text-sm font-bold text-[#0A192F]">True auto top-up</p>
            <p className="mt-1 text-xs text-slate-600 leading-relaxed">
              Off-session charge via saved card / mandate when you fall below
              the threshold. Monthly cap enforced.
            </p>
          </button>
        </div>

        {/* Thresholds + pack */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6"
             data-testid="autotopup-config-panel">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
            Configuration
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Threshold (Diploks)
              </label>
              <input
                type="number" min="1"
                value={config.threshold_diploks}
                onChange={(e) => setConfig({ ...config,
                  threshold_diploks: Number(e.target.value) })}
                onBlur={() => patch({
                  threshold_diploks: config.threshold_diploks })}
                data-testid="autotopup-threshold-input"
                className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Monthly cap (Diploks)
              </label>
              <input
                type="number" min="0"
                value={config.monthly_cap_diploks}
                onChange={(e) => setConfig({ ...config,
                  monthly_cap_diploks: Number(e.target.value) })}
                onBlur={() => patch({
                  monthly_cap_diploks: config.monthly_cap_diploks })}
                data-testid="autotopup-monthlycap-input"
                className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Top-up pack
              </label>
              <select
                value={config.tier_key}
                onChange={(e) => patch({ tier_key: e.target.value })}
                data-testid="autotopup-tier-select"
                className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
              >
                {TIERS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Currency
              </label>
              <select
                value={config.currency}
                onChange={(e) => patch({ currency: e.target.value })}
                data-testid="autotopup-currency-select"
                className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {!config.enabled && (
              <button
                onClick={() => patch({ enabled: true })}
                data-testid="autotopup-enable-btn"
                className="inline-flex items-center gap-2 bg-[#0A192F] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50]"
              >
                <Power className="h-3.5 w-3.5" /> Enable auto top-up
              </button>
            )}
            {config.enabled && config.status === "active" && (
              <button onClick={pauseCfg} data-testid="autotopup-pause-btn"
                      className="inline-flex items-center gap-2 bg-white border border-slate-200 font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-md hover:border-slate-300">
                <PauseCircle className="h-3.5 w-3.5" /> Pause
              </button>
            )}
            {config.status === "paused" && (
              <button onClick={resumeCfg} data-testid="autotopup-resume-btn"
                      className="inline-flex items-center gap-2 bg-emerald-600 text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-md hover:bg-emerald-700">
                <RotateCcw className="h-3.5 w-3.5" /> Resume
              </button>
            )}
            {hasMandate && (
              <button onClick={cancelCfg} data-testid="autotopup-cancel-btn"
                      className="inline-flex items-center gap-2 bg-rose-50 text-rose-700 border border-rose-200 font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-md hover:bg-rose-100">
                Cancel mandate
              </button>
            )}
          </div>
        </div>

        {/* Mandate setup */}
        {config.mode === "true_auto" && !hasMandate && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-5 mb-6"
               data-testid="autotopup-mandate-setup">
            <p className="text-xs uppercase tracking-[0.22em] text-amber-700 font-bold mb-2">
              Mandate required
            </p>
            <p className="text-sm text-slate-700 mb-3">
              Save a payment method to enable off-session auto top-ups. We'll
              never charge more than your monthly cap.
            </p>
            <div className="flex flex-wrap gap-2">
              {config.currency !== "INR" && (
                <button onClick={setupStripeMandate}
                        data-testid="autotopup-stripe-setup"
                        className="inline-flex items-center gap-2 bg-[#0A192F] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50]">
                  <CreditCard className="h-3.5 w-3.5" /> Save card via Stripe
                </button>
              )}
              {config.currency === "INR" && (
                <button onClick={() => {
                  const planId = window.prompt(
                    "Enter your Razorpay plan_id (set up by your organiser):");
                  if (planId) startRazorpaySubscription(planId);
                }}
                        data-testid="autotopup-razorpay-setup"
                        className="inline-flex items-center gap-2 bg-[#0A192F] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50]">
                  <CreditCard className="h-3.5 w-3.5" /> Set up Razorpay mandate
                </button>
              )}
            </div>
          </div>
        )}

        {/* Recent reminders */}
        <div className="rounded-2xl border border-slate-200 bg-white"
             data-testid="autotopup-reminders-panel">
          <div className="px-5 py-4 border-b border-slate-200">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
              Recent reminders
            </p>
          </div>
          {reminders.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-500">
              No reminders yet. We'll alert you when your balance dips below
              your threshold.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {reminders.map((r) => (
                <div key={r.id} className="px-5 py-3 flex items-center gap-3">
                  <Mail className="h-4 w-4 text-[#0F4C81]" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#0A192F]">
                      Balance dipped to {r.balance_at_send} Diploks
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {new Date(r.created_at).toLocaleString()} ·{" "}
                      {r.tier_key} · {r.currency}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="mt-4 text-[11px] text-slate-500 flex items-start gap-1">
          <ShieldCheck className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
          Diploks are platform credits used to unlock eligible digital
          services. Auto top-up charges are limited by your monthly cap and
          can be paused or cancelled at any time. We never store your card
          number — payment methods are held securely by Stripe / Razorpay.
        </p>
      </div>
    </DashboardLayout>
  );
}
